import React, { useState, useEffect } from 'react';
import { MapPin, MessageSquare, User } from 'lucide-react';
import { RegistrationForm } from '../../types';
import { DROPDOWN_OPTIONS, APP_CONFIG } from '../../utils/constants';
import apiService from '../../services/api';

interface FamilyPreferencesStepProps {
  formData: RegistrationForm;
  updateFormData: (data: any) => void;
}

const FamilyPreferencesStep: React.FC<FamilyPreferencesStepProps> = ({ formData, updateFormData }) => {
  const [localData, setLocalData] = useState(formData.familyPreferences);
  const [familyCities, setFamilyCities] = useState<{ value: string; label: string }[]>([]);
  const [loadingFamilyCities, setLoadingFamilyCities] = useState(false);

  useEffect(() => {
    updateFormData({ familyPreferences: localData });
  }, [localData, updateFormData]);

  useEffect(() => {
    if (localData.familyLocation.state) {
      loadFamilyCities(localData.familyLocation.state);
    }
  }, [localData.familyLocation.state]);

  const loadFamilyCities = async (state: string) => {
    setLoadingFamilyCities(true);
    try {
      const response = await apiService.getCities(state);
      setFamilyCities(response.cities || []);
    } catch (error) {
      console.error('Failed to load family cities:', error);
      setFamilyCities([]);
    } finally {
      setLoadingFamilyCities(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('familyLocation.')) {
      const field = name.split('.')[1];
      setLocalData(prev => ({
        ...prev,
        familyLocation: {
          ...prev.familyLocation,
          [field]: value
        }
      }));
    } else {
      setLocalData(prev => ({ ...prev, [name]: value }));
    }
  };

  const getRemainingChars = (field: 'expectations' | 'selfSummary') => {
    const current = localData[field].length;
    return APP_CONFIG.maxDescriptionLength - current;
  };

  return (
    <div className="space-y-6">
      {/* Family Location */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
          <MapPin className="w-5 h-5 text-primary-600" />
          <span>Family Location</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="familyLocation.state" className="block text-sm font-medium text-gray-700 mb-1">
              State *
            </label>
            <select
              name="familyLocation.state"
              value={localData.familyLocation.state}
              onChange={handleChange}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">Select state</option>
              {DROPDOWN_OPTIONS.indianStates.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="familyLocation.city" className="block text-sm font-medium text-gray-700 mb-1">
              City *
            </label>
            <select
              name="familyLocation.city"
              value={localData.familyLocation.city}
              onChange={handleChange}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
              disabled={!localData.familyLocation.state || loadingFamilyCities}
            >
              <option value="">
                {loadingFamilyCities ? 'Loading cities...' : 'Select city'}
              </option>
              {familyCities.map(city => (
                <option key={city.value} value={city.value}>{city.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="familyLocation.address" className="block text-sm font-medium text-gray-700 mb-1">
            Specific Address
          </label>
          <textarea
            name="familyLocation.address"
            value={localData.familyLocation.address}
            onChange={handleChange}
            rows={3}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Enter your family's address (optional)"
          />
        </div>
      </div>

      {/* Partner Expectations */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
          <MessageSquare className="w-5 h-5 text-primary-600" />
          <span>Partner Expectations</span>
        </h3>
        
        <div>
          <label htmlFor="expectations" className="block text-sm font-medium text-gray-700 mb-1">
            What are you looking for in a life partner? *
          </label>
          <textarea
            id="expectations"
            name="expectations"
            value={localData.expectations}
            onChange={handleChange}
            rows={4}
            maxLength={APP_CONFIG.maxDescriptionLength}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Describe your expectations about your ideal life partner, their qualities, values, and what you're looking for in a relationship..."
            required
          />
          <div className="flex justify-between text-xs mt-1">
            <span className="text-gray-500">
              Be specific about your preferences and what matters most to you
            </span>
            <span className={`${getRemainingChars('expectations') < 50 ? 'text-red-500' : 'text-gray-500'}`}>
              {getRemainingChars('expectations')} characters remaining
            </span>
          </div>
        </div>
      </div>

      {/* About Yourself */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
          <User className="w-5 h-5 text-primary-600" />
          <span>About Yourself</span>
        </h3>
        
        <div>
          <label htmlFor="selfSummary" className="block text-sm font-medium text-gray-700 mb-1">
            Tell us about yourself *
          </label>
          <textarea
            id="selfSummary"
            name="selfSummary"
            value={localData.selfSummary}
            onChange={handleChange}
            rows={4}
            maxLength={APP_CONFIG.maxDescriptionLength}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Write about your personality, interests, hobbies, values, what makes you unique, and what kind of person you are..."
            required
          />
          <div className="flex justify-between text-xs mt-1">
            <span className="text-gray-500">
              Share your personality, interests, values, and what makes you special
            </span>
            <span className={`${getRemainingChars('selfSummary') < 50 ? 'text-red-500' : 'text-gray-500'}`}>
              {getRemainingChars('selfSummary')} characters remaining
            </span>
          </div>
        </div>
      </div>

      {/* Tips Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">💡 Writing Tips</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Be honest and authentic in your descriptions</li>
          <li>• Mention your interests, hobbies, and what you enjoy doing</li>
          <li>• Share your values and what's important to you in life</li>
          <li>• Be specific about what you're looking for in a partner</li>
          <li>• Keep it positive and focus on what you want, not what you don't want</li>
        </ul>
      </div>

      {/* Example Section */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2">Example: About Yourself</h4>
        <p className="text-sm text-gray-700 italic">
          "I'm a software engineer who loves reading books and traveling to new places. 
          I value family traditions and enjoy cooking traditional dishes. In my free time, 
          I practice yoga and volunteer at local community events. I believe in maintaining 
          a balance between career and personal life, and I'm looking forward to building 
          a loving family with shared values and mutual respect."
        </p>
      </div>
    </div>
  );
};

export default FamilyPreferencesStep;